import { useCallback, useEffect, useId, useState } from "react";
import { createPortal } from "react-dom";
import { useAuth } from "../../../auth";
import {
  colorTokens,
  getSpacing,
  typographyTokens,
} from "../../../designTokens";
import {
  getUniversalSearchShortcutModifier,
  SEARCH_BUTTON_ID,
} from "./universalSearchShortcutUtils";

const TOOLTIP_OFFSET_PX = 6;

const kbdStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  minWidth: "20px",
  padding: `${getSpacing("2px")} ${getSpacing("4px")}`,
  borderRadius: getSpacing("4px"),
  border: `1px solid ${colorTokens["Borders/Light"]}`,
  backgroundColor: colorTokens["Background/Greyish"],
  fontFamily: typographyTokens["Body/Small"].family,
  fontSize: `${typographyTokens["Body/Small"].size}px`,
  fontWeight: typographyTokens["Body/Small"].weight,
  lineHeight: 1,
  color: colorTokens["Text/Light Grey"],
};

const ShortcutGroup: React.FC<{ mod: string; keyLabel: string }> = ({
  mod,
  keyLabel,
}) => (
  <span style={{ display: "inline-flex", alignItems: "center", gap: getSpacing("4px") }}>
    <kbd style={kbdStyle}>{mod}</kbd>
    <kbd style={kbdStyle}>{keyLabel}</kbd>
  </span>
);

/**
 * Attaches a hover/focus shortcut hint to the existing #my-leads-search-button
 * without modifying MyLeadsHeader. Shown only when the user is logged in.
 */
const UniversalSearchShortcutHint: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const tooltipId = useId();
  const mod = getUniversalSearchShortcutModifier();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [visible, setVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  const updatePosition = useCallback((button: HTMLElement) => {
    const rect = button.getBoundingClientRect();
    setPosition({
      top: rect.bottom + TOOLTIP_OFFSET_PX,
      left: rect.left + rect.width / 2,
    });
  }, []);

  const showHint = useCallback(
    (button: HTMLElement) => {
      updatePosition(button);
      setVisible(true);
      button.setAttribute("aria-describedby", tooltipId);
    },
    [tooltipId, updatePosition]
  );

  const hideHint = useCallback((button: HTMLElement) => {
    setVisible(false);
    button.removeAttribute("aria-describedby");
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      setAnchor(null);
      setVisible(false);
      return;
    }

    let activeButton: HTMLElement | null = null;
    const cleanups: Array<() => void> = [];

    const detachButton = () => {
      cleanups.splice(0).forEach((cleanup) => cleanup());
      if (activeButton) {
        activeButton.removeAttribute("aria-describedby");
        activeButton = null;
      }
      setAnchor(null);
      setVisible(false);
    };

    const attachButton = (button: HTMLElement) => {
      if (activeButton === button) return;
      detachButton();
      activeButton = button;

      const onShow = () => showHint(button);
      const onHide = () => hideHint(button);
      const onReposition = () => {
        if (document.activeElement === button || button.matches(":hover")) {
          updatePosition(button);
        }
      };

      button.addEventListener("mouseenter", onShow);
      button.addEventListener("mouseleave", onHide);
      button.addEventListener("focus", onShow);
      button.addEventListener("blur", onHide);
      window.addEventListener("resize", onReposition);
      window.addEventListener("scroll", onReposition, true);

      cleanups.push(() => {
        button.removeEventListener("mouseenter", onShow);
        button.removeEventListener("mouseleave", onHide);
        button.removeEventListener("focus", onShow);
        button.removeEventListener("blur", onHide);
        window.removeEventListener("resize", onReposition);
        window.removeEventListener("scroll", onReposition, true);
      });

      setAnchor(button);
    };

    const syncButton = () => {
      const button = document.getElementById(SEARCH_BUTTON_ID);
      if (button instanceof HTMLElement) {
        attachButton(button);
        return;
      }
      detachButton();
    };

    syncButton();

    const observer = new MutationObserver(syncButton);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      detachButton();
    };
  }, [hideHint, isAuthenticated, showHint, updatePosition]);

  if (!isAuthenticated || !anchor || !visible) {
    return null;
  }

  return createPortal(
    <div
      id={tooltipId}
      role="tooltip"
      style={{
        position: "fixed",
        top: position.top,
        left: position.left,
        transform: "translateX(-50%)",
        zIndex: 1000,
        display: "flex",
        alignItems: "center",
        gap: getSpacing("8px"),
        padding: `${getSpacing("4px")} ${getSpacing("8px")}`,
        borderRadius: getSpacing("8px"),
        border: `1px solid ${colorTokens["Borders/Light"]}`,
        backgroundColor: colorTokens["Background/White"],
        boxShadow: "0 4px 12px rgba(8, 15, 26, 0.12)",
        pointerEvents: "none",
        whiteSpace: "nowrap",
      }}
    >
      <ShortcutGroup mod={mod} keyLabel="/" />
    </div>,
    document.body
  );
};

export default UniversalSearchShortcutHint;
